import { ref, computed } from 'vue'
import Swal from 'sweetalert2'
import type { WaTpl } from '@/types/chat'
import type { CwContact } from '@/types/templates'
import { useAdmin } from '@/composables/useAdmin'
import { TEMPLATE_PAYLOAD_VARS, loadTemplateVarMapping, resolveTemplateVarValue } from '@/utils/templateVars'

// Chamada direta ao Graph API da Meta a partir do frontend, com o token exposto no
// bundle — comportamento mantido como estava (decisão de roadmap: só deduplicar
// agora, endereçar a exposição do token é item separado).
const META_VERSION  = process.env.VUE_APP_META_VERSION  || 'v25.0'
const META_WABA_ID  = process.env.VUE_APP_META_WABA_ID  || ''
const META_TOKEN    = process.env.VUE_APP_META_TOKEN    || ''
const META_PHONE_ID = process.env.VUE_APP_META_PHONE_ID || ''
const META_BASE     = `https://graph.facebook.com/${META_VERSION}`

export interface UseWhatsappTemplatesOptions {
  /** Telefone do contato da conversa atual (com ou sem formatação — os não-dígitos são removidos aqui). */
  getContactPhone: () => string | undefined | null
  /** Estado atual da janela de 24h da conversa (ver useMessageWindow) — usado só quando
   * "Envio de Template Restrito" está ativo em Admin → Configurações Gerais. */
  isWindowClosed?: () => boolean
  /** Contato da conversa atual — usado para resolver variáveis vinculadas (ver Admin → Templates → Vincular Variáveis). */
  getContact?: () => { id?: number | string; name?: string; phone?: string; email?: string } | undefined | null
}

/** Modal de seleção e envio de templates de WhatsApp via Graph API da Meta. */
export function useWhatsappTemplates(options: UseWhatsappTemplatesOptions) {
  const { globalSettings } = useAdmin()
  const tplOpen      = ref(false)
  const tplsLoading   = ref(false)
  const tplsAll       = ref<WaTpl[]>([])
  const tplsApproved  = computed(() => tplsAll.value.filter(t => t.status === 'APPROVED'))
  const tplSel        = ref<WaTpl | null>(null)
  const tplVarNums    = ref<number[]>([])
  const tplVars       = ref<Record<number, string>>({})
  const tplVarMapping = ref<Record<number, string>>({})
  const tplImgUrl     = ref('')
  const tplImgErr     = ref(false)
  const tplSending    = ref(false)
  const tplTime       = new Date().getHours().toString().padStart(2, '0') + ':' + new Date().getMinutes().toString().padStart(2, '0')

  // Mesma regra usada em sendTpl(), exposta como computed pra os componentes
  // desabilitarem o botão de envio (com tooltip) em vez de só barrar no clique.
  const tplSendBlocked = computed(() =>
    !!globalSettings.value.restrictedTemplateSendingEnabled && !options.isWindowClosed?.()
  )
  const tplSendBlockedReason = computed(() => tplSendBlocked.value
    ? 'Envio de template restrito ativado: só é possível enviar quando a janela de 24h do WhatsApp já estiver vencida.'
    : '')

  const tplHasImg    = computed(() => !!tplSel.value?.components.some((c: any) => c.type === 'HEADER' && c.format === 'IMAGE'))
  const tplHeaderTxt = computed(() => tplSel.value?.components.find((c: any) => c.type === 'HEADER' && c.format === 'TEXT')?.text || '')
  const tplFooterTxt = computed(() => tplSel.value?.components.find((c: any) => c.type === 'FOOTER')?.text || '')
  const tplButtons   = computed(() => tplSel.value?.components.find((c: any) => c.type === 'BUTTONS')?.buttons || [])
  const tplBodyRaw   = computed(() => tplSel.value?.components.find((c: any) => c.type === 'BODY')?.text || '')
  const tplPreviewHtml = computed(() => {
    let t = tplBodyRaw.value
    for (const n of tplVarNums.value) {
      if (tplVarMapping.value[n]) {
        t = t.replace(new RegExp(`\\{\\{${n}\\}\\}`, 'g'), `<mark class="var-highlight">${mappedLabel(n)}</mark>`)
        continue
      }
      const val = tplVars.value[n]
      const repl = val ? `<mark class="var-highlight">${val}</mark>` : `<mark class="var-highlight">{{${n}}}</mark>`
      t = t.replace(new RegExp(`\\{\\{${n}\\}\\}`, 'g'), repl)
    }
    return t.replace(/\n/g, '<br/>')
  })

  function mappedLabel(n: number): string {
    return TEMPLATE_PAYLOAD_VARS.find(v => v.key === tplVarMapping.value[n])?.label || ''
  }

  function openTplModal() {
    tplOpen.value    = true
    tplSel.value     = null
    tplVarNums.value = []
    tplVars.value    = {}
    tplVarMapping.value = {}
    tplImgUrl.value  = ''
    tplImgErr.value  = false
    tplSending.value = false
    if (!tplsAll.value.length) fetchTpls()
  }

  function closeTplModal() { tplOpen.value = false }

  async function fetchTpls() {
    tplsLoading.value = true
    try {
      const url = `${META_BASE}/${META_WABA_ID}/message_templates?access_token=${META_TOKEN}&fields=name,status,category,language,components&limit=100`
      const res  = await fetch(url)
      const data = await res.json()
      tplsAll.value = data.data || []
    } catch { tplsAll.value = [] } finally { tplsLoading.value = false }
  }

  function selectTpl(t: WaTpl) {
    tplSel.value = t
    const bodyRaw = t.components.find((c: any) => c.type === 'BODY')?.text || ''
    tplVarNums.value = [...new Set([...(bodyRaw.matchAll(/\{\{(\d+)\}\}/g))].map(m => Number(m[1])))].sort((a, b) => a - b)
    tplVars.value   = {}
    tplVarMapping.value = loadTemplateVarMapping(t.id)
    tplImgErr.value = false
    const hdr = t.components.find((c: any) => c.type === 'HEADER')
    tplImgUrl.value = hdr?.example?.header_handle?.[0] || ''
  }

  function setTplVar(n: number, val: string) { tplVars.value = { ...tplVars.value, [n]: val } }

  async function sendTpl() {
    if (!tplSel.value || tplSending.value) return
    // Envio de Template Restrito (Admin → Configurações Gerais): só permite template
    // com a janela de 24h já vencida — em qualquer outra situação, bloqueia aqui mesmo,
    // que é o único caminho de envio real (Graph API), independente do botão que chamou.
    if (tplSendBlocked.value) {
      Swal.fire({
        title: 'Envio de template restrito',
        text: 'Com "Envio de Template Restrito" ativado em Configurações Gerais, só é possível enviar templates quando a janela de 24 horas do WhatsApp já estiver vencida.',
        icon: 'warning',
      })
      return
    }
    const phone = options.getContactPhone()?.replace(/\D/g, '')
    if (!phone) { Swal.fire({ title: 'Sem telefone', text: 'Contato sem número de telefone.', icon: 'warning' }); return }
    tplSending.value = true
    try {
      const t = tplSel.value
      const components: any[] = []
      if (tplHasImg.value && tplImgUrl.value)
        components.push({ type: 'header', parameters: [{ type: 'image', image: { link: tplImgUrl.value } }] })
      if (tplVarNums.value.length) {
        const contact = options.getContact?.()
        components.push({ type: 'body', parameters: tplVarNums.value.map(n => {
          const mappedKey = tplVarMapping.value[n]
          if (mappedKey && contact) {
            const cwContact: CwContact = { id: Number(contact.id) || 0, name: contact.name || '', phone_number: contact.phone || '', email: contact.email }
            return { type: 'text', text: resolveTemplateVarValue(mappedKey, cwContact) }
          }
          return { type: 'text', text: tplVars.value[n] || '' }
        }) })
      }
      const payload = {
        messaging_product: 'whatsapp', to: phone, type: 'template',
        template: { name: t.name, language: { code: t.language }, ...(components.length ? { components } : {}) },
      }
      const res  = await fetch(`${META_BASE}/${META_PHONE_ID}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${META_TOKEN}` },
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (data.error) { Swal.fire({ title: 'Erro ao enviar', text: data.error.error_user_msg || data.error.message || 'Erro ao enviar', icon: 'error' }) }
      else closeTplModal()
    } catch (e: any) { Swal.fire({ title: 'Erro ao enviar', text: e.message || 'Erro ao enviar', icon: 'error' }) }
    finally { tplSending.value = false }
  }

  return {
    tplOpen, tplsLoading, tplsAll, tplsApproved, tplSel, tplVarNums, tplVars, tplVarMapping, tplImgUrl, tplImgErr, tplSending, tplTime,
    tplHasImg, tplHeaderTxt, tplFooterTxt, tplButtons, tplBodyRaw, tplPreviewHtml,
    tplSendBlocked, tplSendBlockedReason,
    openTplModal, closeTplModal, fetchTpls, selectTpl, setTplVar, sendTpl, mappedLabel,
  }
}
